<template>
  <div class="comment-mgmt">
    <h2>评论管理</h2>
    <el-form :inline="true" class="filter">
      <el-form-item label="作品ID">
        <el-input v-model.number="filter.game_id" placeholder="game_id" clearable size="small" />
      </el-form-item>
      <el-form-item label="用户ID">
        <el-input v-model.number="filter.user_id" placeholder="user_id" clearable size="small" />
      </el-form-item>
      <el-form-item label="关键词">
        <el-input v-model="filter.keyword" placeholder="内容关键词" clearable size="small" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="filter.status" placeholder="全部" clearable size="small" style="width: 110px">
          <el-option label="可见" :value="1" />
          <el-option label="已隐藏" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-checkbox v-model="onlyPinned">仅置顶</el-checkbox>
      </el-form-item>
      <el-form-item>
        <el-checkbox v-model="onlyFeatured">仅精选</el-checkbox>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" size="small" @click="reload">查询</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="items" v-loading="loading" border size="small">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column label="作品" width="160">
        <template slot-scope="{row}">{{ row.game.title }} (#{{ row.game.id }})</template>
      </el-table-column>
      <el-table-column label="用户" width="160">
        <template slot-scope="{row}">
          <img v-if="row.user.avatar" :src="row.user.avatar" style="width:24px;height:24px;vertical-align:middle;border-radius:50%" />
          {{ row.user.nickname }} (#{{ row.user.id }})
        </template>
      </el-table-column>
      <el-table-column label="内容">
        <template slot-scope="{row}">
          <div style="white-space:pre-wrap; max-height: 80px; overflow: auto">{{ row.content }}</div>
        </template>
      </el-table-column>
      <el-table-column label="回复目标" width="240">
        <template slot-scope="{row}">
          <span v-if="!row.parent_id" style="color:#909399">—</span>
          <div v-else style="line-height:1.4">
            <div>
              回复 @{{ (row.reply_to_user || (row.parent && row.parent.user) || {}).nickname || '用户' }}
              <span style="color:#909399"> · #{{ row.parent_id }}</span>
            </div>
            <el-tooltip v-if="row.parent" effect="dark" placement="top">
              <div slot="content" style="max-width:320px; white-space:pre-wrap">{{ row.parent.content }}</div>
              <div style="color:#909399; font-size:12px; max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap">
                «{{ row.parent.content }}»
              </div>
            </el-tooltip>
            <div v-else style="color:#c0c4cc; font-size:12px">[原评论已删除]</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="like_count" label="赞" width="60" />
      <el-table-column prop="reply_count" label="回复" width="60" />
      <el-table-column label="标记" width="100">
        <template slot-scope="{row}">
          <el-tag v-if="row.is_pinned" type="success" size="mini">置顶</el-tag>
          <el-tag v-if="row.is_featured" type="warning" size="mini">精选</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="80">
        <template slot-scope="{row}">
          <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
            {{ row.status === 1 ? '可见' : '已隐藏' }}
          </el-tag>
          <el-tag v-if="row.deleted_at" type="danger" size="mini">已删</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="时间" width="160" />
      <el-table-column label="操作" width="280">
        <template slot-scope="{row}">
          <el-button size="mini" @click="togglePin(row)">{{ row.is_pinned ? '取消置顶' : '置顶' }}</el-button>
          <el-button size="mini" @click="toggleFeatured(row)">{{ row.is_featured ? '取消精选' : '精选' }}</el-button>
          <el-button size="mini" type="warning" @click="toggleStatus(row)">{{ row.status === 1 ? '隐藏' : '取消隐藏' }}</el-button>
          <el-button size="mini" type="danger" @click="onDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      style="margin-top: 12px"
      layout="prev, pager, next, total"
      :total="total" :page-size="pageSize" :current-page.sync="page"
      @current-change="reload"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import {
  listComments, patchComment, deleteComment,
  AdminComment, ListCommentParams,
} from '@/api/comment'

@Component
export default class CommentManagement extends Vue {
  filter: Partial<ListCommentParams> = {}
  onlyPinned = false
  onlyFeatured = false
  items: AdminComment[] = []
  loading = false
  page = 1
  pageSize = 20
  total = 0

  mounted() { this.reload() }

  async reload() {
    this.loading = true
    try {
      const params: ListCommentParams = {
        page: this.page, page_size: this.pageSize,
        game_id: this.filter.game_id || undefined,
        user_id: this.filter.user_id || undefined,
        keyword: this.filter.keyword || undefined,
        status: this.filter.status,
        pinned: this.onlyPinned ? '1' : '',
        featured: this.onlyFeatured ? '1' : '',
      }
      const res = await listComments(params)
      this.items = res.items
      this.total = res.total
    } finally { this.loading = false }
  }

  async togglePin(row: AdminComment) {
    await patchComment(row.id, { is_pinned: row.is_pinned ? 0 : 1 })
    row.is_pinned = !row.is_pinned
  }
  async toggleFeatured(row: AdminComment) {
    await patchComment(row.id, { is_featured: row.is_featured ? 0 : 1 })
    row.is_featured = !row.is_featured
  }
  async toggleStatus(row: AdminComment) {
    const newStatus: 1 | 2 = row.status === 1 ? 2 : 1
    await patchComment(row.id, { status: newStatus })
    row.status = newStatus
  }
  async onDelete(row: AdminComment) {
    try {
      await this.$confirm('确认删除该评论？', '提示', { type: 'warning' })
    } catch { return }
    await deleteComment(row.id)
    this.items = this.items.filter(c => c.id !== row.id)
    this.$message.success('已删除')
  }
}
</script>

<style scoped>
.comment-mgmt { padding: 16px; }
.filter { margin-bottom: 12px; }
</style>
